import { Mail, MapPin } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import emailjs from '@emailjs/browser';

function Contact() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm();

  async function onSubmit(data: any) {
    try {
      // Send email to admin
      await emailjs.send(
        'service_7qha3vx',
        'admin',
        {
          name: data.name,
          email: data.email,
          phone: data.phone,
          message: data.message,
        },
        '4dj2gUeFLMea-Fpph'
      );

      // Send confirmation email to user
      await emailjs.send(
        'service_7qha3vx',
        'user', // <-- Replace with your user confirmation template ID
        {
          name: data.name,
          email: data.email,
          message: data.message,
        },
        '4dj2gUeFLMea-Fpph'
      );

      toast.success("Submitted!");
      reset();
    } catch (error) {
      toast.error("Failed to send email. Please try again.");
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Main Content Area */}
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="relative pt-16 md:pt-24 text-center">
            <div className="relative z-10 px-4 sm:px-6 lg:px-8">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Get in Touch With Us
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-xl mx-auto">
                We are here to support you! Your questions and comments are
                important.
              </p>
            </div>
          </div>
          {/* Contact Info and Form Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="flex flex-col lg:flex-row lg:space-x-12">
              {/* Contact Information Card */}
              <div className="lg:w-1/3 mb-12 lg:mb-0 bg-sky-100 text-gray-900 p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-semibold mb-4">
                  Contact Information
                </h2>
                <p className="text-base mb-8">
                  <p><b>Genes – Wellness, Your Way</b></p>
                  Genes is your personal wellness companion.
                  <br/>Chat free with experts. Get:
                  <ul>
                    <li>Genuine advice</li>
                    <li>Explore health content</li>
                    <li>Discuss your health and wellness issues with experts</li>
                    <li>Discover products that fit your lifestyle</li>
                  </ul>
                  Whether you're focusing on mental health, fitness, or nutrition – Genes is here to guide you to your healthy, happy life.
                  <br />Feel better, live better — with Genes.
                </p>
                <div className="space-y-6">
                  <div className="flex items-start space-x-3">
                    <Mail className="text-xl mt-1" />
                    <a
                      href="mailto:support@mapyourfreedom.com"
                      className="text-base font-normal hover:underline"
                    >
                      quaid@qjskills.com
                    </a>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="text-xl mt-1" />
                    <address className="text-base font-normal not-italic">
                      Suite 102, Anum Pride A, <br/>K.C.H.S. Cooperative, Block 7/8, <br/>Saheed-e-Millat Rd.,
Karachi, Sindh, Pakistan
                    </address>
                  </div>
                  {/* Optional Phone Number */}
                  {/* <div className="flex items-start space-x-3">
                         <FaPhoneAlt className="text-xl mt-1" />
                         <span className="text-base font-normal" >
                           +1 123 456 7890
                         </span>
                       </div> */}
                </div>
              </div>
              {/* Contact Form */}
              <div className="lg:w-2/3">
                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                  <div>
                    <label
                      htmlFor="first-name"
                      className="block font-semibold text-gray-700"
                    >
                      Full Name
                    </label>
                    <input
                      {...register("name", { required: "Full Name is required" })}
                      type="text"
                      name="name"
                      id="name"
                      autoComplete="given-name"
                      placeholder="John"
                      className="mt-1 block w-full px-3 py-2 border-b focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                    {errors?.name?.message && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.name.message)}</p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block font-semibold text-gray-700"
                    >
                      Email
                    </label>
                    <input
                      {...register("email", { required: "Email is required" })}
                      type="email"
                      name="email"
                      id="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="mt-1 block w-full px-3 py-2 border-b focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                    {errors?.email?.message && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.email.message)}</p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block font-semibold text-gray-700"
                    >
                      Phone Number (Optional)
                    </label>
                    <input
                      {...register("phone")}
                      type="tel"
                      name="phone"
                      id="phone"
                      autoComplete="tel"
                      placeholder="+1 012 3456 789"
                      className="mt-1 block w-full px-3 py-2 border-b focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                    {/* {errors?.phone && (
                      <p className="error">{errors?.phone?.message}</p>
                    )} */}
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block font-semibold text-gray-700"
                    >
                      Message
                    </label>
                    <textarea
                      {...register("message", { required: "Message is required" })}
                      id="message"
                      name="message"
                      rows={4}
                      placeholder="Write your message.."
                      className="mt-1 block w-full px-3 py-2 border-b focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    ></textarea>
                    {errors?.message?.message && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.message.message)}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <button
                      disabled={isSubmitting}
                      type="submit"
                      className="inline-flex justify-center py-3 px-8 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition duration-150 ease-in-out disabled:bg-slate-400 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Submitting" : "Send Message"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Contact;
